import { useState } from "react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "./ui/accordion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";

/**
 * FAQ component that can be used in two modes:
 * 1. With tabs (students + universities): pass { student: [...], university: [...] }
 * 2. Single list (no tabs): pass items directly as an array
 */
export default function FAQ({
    tag = "Got Questions?",
    headline = 'We\'ve got <span class="text-aiblue">answers.</span>',
    items,
    tabs = false,
}) {
    const [tab, setTab] = useState("student");

    // If items is an array (not tabbed), render a simple accordion
    const isTabbed =
        tabs && items && typeof items === "object" && !Array.isArray(items);

    return (
        <section className="py-16 bg-white scroll-mt-28" data-testid="faq-section" id="faq">
            <div className="max-w-4xl mx-auto px-4 sm:px-8">
                <div className="text-center mb-12">
                    <p className="text-xs uppercase tracking-[0.3em] text-aiorange font-bold mb-3">
                        {tag}
                    </p>
                    <h2
                        className="font-display font-black text-4xl sm:text-5xl tracking-normal"
                        dangerouslySetInnerHTML={{ __html: headline }}
                    />
                </div>

                {isTabbed ? (
                    <Tabs value={tab} onValueChange={setTab}>
                        <TabsList
                            className="grid grid-cols-2 w-full max-w-sm mx-auto mb-10 bg-aibeige/50 p-1.5 rounded-full h-auto"
                            data-testid="faq-tabs"
                        >
                            <TabsTrigger
                                value="student"
                                data-testid="faq-tab-student"
                                className="rounded-full data-[state=active]:bg-aiblue text-black py-2"
                            >
                                Students
                            </TabsTrigger>
                            <TabsTrigger
                                value="university"
                                data-testid="faq-tab-university"
                                className="rounded-full data-[state=active]:bg-aiblue text-black py-2"
                            >
                                Universities
                            </TabsTrigger>
                        </TabsList>
                        {Object.entries(items).map(([k, faqItems]) => (
                            <TabsContent key={k} value={k}>
                                <Accordion
                                    type="single"
                                    collapsible
                                    className="space-y-3"
                                >
                                    {faqItems.map((it, i) => (
                                        <AccordionItem
                                            key={i}
                                            value={`${k}-${i}`}
                                            className="border border-gray-200 rounded-2xl px-5 data-[state=open]:border-aiblue data-[state=open]:bg-aiblue/5"
                                        >
                                            <AccordionTrigger
                                                className="font-display font-semibold text-left hover:no-underline py-5"
                                                data-testid={`faq-q-${k}-${i}`}
                                            >
                                                {it.q}
                                            </AccordionTrigger>
                                            <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                                                {it.a}
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </TabsContent>
                        ))}
                    </Tabs>
                ) : (
                    <Accordion type="single" collapsible className="space-y-3">
                        {(Array.isArray(items) ? items : []).map((it, i) => (
                            <AccordionItem
                                key={i}
                                value={`faq-${i}`}
                                className="border border-gray-200 rounded-2xl px-5 data-[state=open]:border-aiblue data-[state=open]:bg-aiblue/5"
                            >
                                <AccordionTrigger
                                    className="font-display font-semibold text-left hover:no-underline py-5"
                                    data-testid={`faq-q-${i}`}
                                >
                                    {it.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                                    {it.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                )}
            </div>
        </section>
    );
}
